import type { TemplateType } from '@/types/api.types';

interface VariableEntry {
  name: string;
  defaultValue: string;
}

interface ContactEntry {
  formattedName: string;
  phones: string;
  emails: string;
}

interface PreviewProps {
  type: TemplateType;
  text?: string;
  variables?: VariableEntry[];
  zenviaTemplateId?: string;
  fileUrl?: string;
  fileMimeType?: string;
  fileCaption?: string;
  contacts?: ContactEntry[];
  latitude?: string;
  longitude?: string;
  locationName?: string;
  locationAddress?: string;
}

function renderText(text: string, variables: VariableEntry[]): string {
  return text.replace(/\{\{(\w+)\}\}/g, (_, name) => {
    const found = variables.find((v) => v.name === name);
    return found?.defaultValue ? found.defaultValue : `[${name}]`;
  });
}

export function TemplatePreview(props: PreviewProps) {
  const { type, variables = [] } = props;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Preview
      </p>

      <div className="rounded-lg border bg-muted/30 p-4 text-sm">
        {type === 'text' && (
          <div className="space-y-2">
            <div className="rounded-lg bg-white px-4 py-3 shadow-sm dark:bg-muted">
              <p className="whitespace-pre-wrap leading-relaxed">
                {props.text
                  ? renderText(props.text, variables)
                  : <span className="italic text-muted-foreground">Conteúdo aparecerá aqui…</span>}
              </p>
            </div>
            {variables.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Variáveis detectadas:</p>
                {variables.map((v) => (
                  <div key={v.name} className="flex items-center gap-2 text-xs">
                    <code className="rounded bg-primary/10 px-1 text-primary">{`{{${v.name}}}`}</code>
                    <span className="text-muted-foreground">→</span>
                    <span>{v.defaultValue || <em className="text-muted-foreground">(vazio)</em>}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {type === 'template' && (
          <div className="space-y-3">
            <div>
              <p className="text-xs text-muted-foreground">ID Zenvia</p>
              <code className="text-sm">
                {props.zenviaTemplateId || <em className="text-muted-foreground">não informado</em>}
              </code>
            </div>
            {variables.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Variáveis:</p>
                {variables.map((v) => (
                  <div key={v.name} className="flex items-center gap-2 text-xs">
                    <code className="rounded bg-primary/10 px-1 text-primary">{v.name}</code>
                    <span className="text-muted-foreground">=</span>
                    <span>{v.defaultValue || <em className="text-muted-foreground">(sem default)</em>}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {type === 'file' && (
          <div className="space-y-2">
            {props.fileUrl ? (
              <a
                href={props.fileUrl}
                target="_blank"
                rel="noreferrer"
                className="break-all text-primary underline-offset-4 hover:underline"
              >
                {props.fileUrl}
              </a>
            ) : (
              <p className="italic text-muted-foreground">URL não informada</p>
            )}
            {props.fileMimeType && (
              <p className="text-xs text-muted-foreground">Tipo: {props.fileMimeType}</p>
            )}
            {props.fileCaption && (
              <p className="rounded bg-white px-3 py-2 shadow-sm dark:bg-muted">{props.fileCaption}</p>
            )}
          </div>
        )}

        {type === 'contact' && (
          <div className="space-y-2">
            {(!props.contacts || props.contacts.length === 0) ? (
              <p className="italic text-muted-foreground">Nenhum contato adicionado</p>
            ) : (
              props.contacts.map((c, i) => (
                <div key={i} className="rounded border bg-white p-3 shadow-sm dark:bg-muted">
                  <p className="font-medium">{c.formattedName || '—'}</p>
                  {c.phones && <p className="text-xs text-muted-foreground">Tel: {c.phones}</p>}
                  {c.emails && <p className="text-xs text-muted-foreground">Email: {c.emails}</p>}
                </div>
              ))
            )}
          </div>
        )}

        {type === 'location' && (
          <div className="space-y-1">
            <p className="font-medium">{props.locationName || 'Localização'}</p>
            {props.locationAddress && (
              <p className="text-xs text-muted-foreground">{props.locationAddress}</p>
            )}
            <p className="text-xs font-mono text-muted-foreground">
              {props.latitude || '—'}, {props.longitude || '—'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
